import { useEffect, useState } from 'react';
import api from '@/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Download, FileCheck2, ShieldAlert, Upload } from 'lucide-react';

const today = () => new Date().toISOString().slice(0, 10);
const statusLabels = {
  not_received: 'Не получено',
  received: 'Получено',
  revoked: 'Отозвано',
};

const errorMessage = (error, fallback) => {
  const detail = error.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail?.message) return detail.message;
  return fallback;
};

const downloadBlob = async (url, fallbackName) => {
  const response = await api.get(url, { responseType: 'blob' });
  const objectUrl = URL.createObjectURL(response.data);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = fallbackName;
  anchor.click();
  URL.revokeObjectURL(objectUrl);
};

export function PatientConsentDialog({ open, onOpenChange, patient, onUpdated, canManageTemplate = false, gate = false }) {
  const [consent, setConsent] = useState(null);
  const [legalDocument, setLegalDocument] = useState(null);
  const [receivedAt, setReceivedAt] = useState(today());
  const [scan, setScan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [templateTitle, setTemplateTitle] = useState('Согласие на обработку специальных категорий персональных данных');
  const [templateVersion, setTemplateVersion] = useState('1.0');
  const [templateFile, setTemplateFile] = useState(null);

  const load = async () => {
    if (!patient) return;
    setLoading(true);
    try {
      const [consentResponse, documentResponse] = await Promise.all([
        api.get(`/patients/${patient.id}/consents/special-categories`),
        api.get('/consents/legal-documents/current'),
      ]);
      setConsent(consentResponse.data.consent);
      setLegalDocument(documentResponse.data.document);
      if (consentResponse.data.consent?.received_at) {
        setReceivedAt(String(consentResponse.data.consent.received_at).slice(0, 10));
      }
    } catch (error) {
      toast.error(errorMessage(error, 'Не удалось загрузить сведения о согласии'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    setScan(null);
    setReceivedAt(today());
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, patient?.id]);

  const confirm = async () => {
    if (!scan) { toast.error('Прикрепите скан бумажного согласия'); return; }
    if (!legalDocument) { toast.error('Сначала загрузите актуальный шаблон согласия'); return; }
    const form = new FormData();
    form.append('received_at', receivedAt);
    form.append('verification_method', 'paper');
    form.append('legal_document_id', legalDocument.id);
    form.append('file', scan);
    setSaving(true);
    try {
      const { data } = await api.post(`/patients/${patient.id}/consents/special-categories/confirm`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setConsent(data.consent);
      setScan(null);
      toast.success('Согласие подтверждено');
      await onUpdated?.();
    } catch (error) {
      toast.error(errorMessage(error, 'Не удалось подтвердить согласие'));
    } finally {
      setSaving(false);
    }
  };

  const revoke = async () => {
    if (!window.confirm('Отозвать согласие? Новые чувствительные данные будут заблокированы.')) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/patients/${patient.id}/consents/special-categories/revoke`, { reason: '' });
      setConsent(data.consent);
      toast.success('Согласие отозвано; данные поставлены на проверку срока хранения');
      await onUpdated?.();
    } catch (error) {
      toast.error(errorMessage(error, 'Не удалось отозвать согласие'));
    } finally {
      setSaving(false);
    }
  };

  const uploadTemplate = async () => {
    if (!templateTitle.trim() || !templateVersion.trim() || !templateFile) {
      toast.error('Укажите название, версию и файл шаблона');
      return;
    }
    const form = new FormData();
    form.append('title', templateTitle.trim());
    form.append('version', templateVersion.trim());
    form.append('consent_type', 'special_categories');
    form.append('file', templateFile);
    setSaving(true);
    try {
      const { data } = await api.post('/consents/legal-documents', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setLegalDocument(data.document);
      setTemplateFile(null);
      toast.success('Шаблон согласия опубликован');
    } catch (error) {
      toast.error(errorMessage(error, 'Не удалось загрузить шаблон'));
    } finally {
      setSaving(false);
    }
  };

  const status = consent?.status || 'not_received';
  const isReceived = status === 'received';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[calc(100dvh-2rem)] max-w-lg overflow-y-auto border-stone-800 bg-stone-900 text-stone-200">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading text-stone-100">
            {isReceived ? <FileCheck2 className="text-emerald-500" size={19} /> : <ShieldAlert className="text-amber-500" size={19} />}
            Согласие клиента
          </DialogTitle>
        </DialogHeader>

        {gate && (
          <div className="rounded-lg border border-amber-900/60 bg-amber-950/25 p-3 text-sm text-amber-200">
            Для хранения чувствительной информации необходимо подтвердить наличие соответствующего согласия клиента
          </div>
        )}

        <div className="space-y-4">
          <div className="rounded-lg border border-stone-800 bg-stone-950/35 p-3">
            <p className="text-sm font-medium text-stone-200">{patient?.name}</p>
            <p className="mt-1 text-xs text-stone-500">Статус: <span className={isReceived ? 'text-emerald-400' : status === 'revoked' ? 'text-red-400' : 'text-amber-400'}>{statusLabels[status]}</span></p>
            {consent?.legal_document_version && <p className="mt-1 text-xs text-stone-600">Версия документа: {consent.legal_document_version}</p>}
          </div>

          {legalDocument ? (
            <Button type="button" variant="outline" className="w-full border-stone-700 text-stone-300" onClick={async () => {
              try { await downloadBlob(`/consents/legal-documents/${legalDocument.id}/download`, `consent-${legalDocument.version}.pdf`); }
              catch (error) { toast.error(errorMessage(error, 'Не удалось скачать шаблон')); }
            }}>
              <Download size={14} className="mr-2" /> Скачать шаблон · версия {legalDocument.version}
            </Button>
          ) : (
            <div className="rounded-lg border border-stone-800 p-3 text-xs text-stone-500">
              Актуальный шаблон согласия ещё не настроен.
            </div>
          )}

          {!legalDocument && canManageTemplate && (
            <div className="space-y-3 rounded-lg border border-stone-800 bg-stone-950/25 p-3">
              <p className="text-xs font-medium text-stone-300">Опубликовать версионируемый шаблон</p>
              <Input value={templateTitle} onChange={event => setTemplateTitle(event.target.value)} placeholder="Название документа" className="border-stone-800 bg-stone-950/50" />
              <Input value={templateVersion} onChange={event => setTemplateVersion(event.target.value)} placeholder="Версия, например 1.0" className="border-stone-800 bg-stone-950/50" />
              <Input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={event => setTemplateFile(event.target.files?.[0] || null)} className="border-stone-800 bg-stone-950/50" />
              <Button type="button" variant="outline" disabled={saving} onClick={uploadTemplate} className="border-stone-700 text-stone-300">
                <Upload size={14} className="mr-2" /> Загрузить шаблон
              </Button>
            </div>
          )}

          <div className="space-y-3 border-t border-stone-800 pt-4">
            <div>
              <label className="mb-1.5 block text-xs font-medium text-stone-400">Дата получения</label>
              <Input type="date" max={today()} value={receivedAt} onChange={event => setReceivedAt(event.target.value)} className="border-stone-800 bg-stone-950/50" />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-medium text-stone-400">Способ подтверждения</label>
              <Input value="Бумажное согласие" disabled className="border-stone-800 bg-stone-950/50 text-stone-500" />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-medium text-stone-400">Скан согласия <span className="text-red-400">*</span></label>
              <Input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={event => setScan(event.target.files?.[0] || null)} className="border-stone-800 bg-stone-950/50" />
            </div>
          </div>

          {isReceived && consent.document_file_id && (
            <Button type="button" variant="ghost" className="w-full text-stone-400" onClick={async () => {
              try { await downloadBlob(`/patients/${patient.id}/consents/documents/${consent.document_file_id}/download`, `consent-${patient.id}.pdf`); }
              catch (error) { toast.error(errorMessage(error, 'Не удалось скачать скан')); }
            }}>
              <Download size={14} className="mr-2" /> Скачать сохранённый скан
            </Button>
          )}
        </div>

        <DialogFooter className="gap-2 sm:justify-between">
          <div>{isReceived && <Button type="button" variant="ghost" disabled={saving} onClick={revoke} className="text-red-400 hover:bg-red-950/30 hover:text-red-300">Отозвать</Button>}</div>
          <div className="flex gap-2">
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)} className="bg-stone-800 text-stone-300">Отмена</Button>
            <Button type="button" disabled={saving || loading || !legalDocument} onClick={confirm} className="bg-amber-700 text-white hover:bg-amber-600">
              {saving ? 'Сохранение…' : isReceived ? 'Заменить документ' : 'Подтвердить согласие'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
